/**
 * Formats a Date object, Unix timestamp, or ISO string to Romanian locale date/time string
 * @param input - Date object, Unix timestamp in seconds, or ISO date string
 * @returns Formatted date string in Romanian locale or null if invalid
 * @example formatUnixTimestamp(new Date()) => "1 ianuarie 2022, 00:00"
 * @example formatUnixTimestamp("2025-10-26T17:56:16.318449") => "26 octombrie 2025, 17:56"
 */
export function formatUnixTimestamp(input: Date | number | string | null | undefined): string | null {
    if (!input) return null;

    // Handle Date objects
    if (input instanceof Date) {
        if (isNaN(input.getTime())) return null;
        return input.toLocaleString("ro-RO", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        });
    }

    // Handle ISO date strings (e.g., "2025-10-26T17:56:16.318449")
    if (typeof input === 'string') {
        const date = new Date(input);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleString("ro-RO", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        });
    }

    // Handle Unix timestamps (legacy - in seconds)
    if (typeof input === 'number') {
        if (isNaN(input)) return null;
        const date = new Date(input * 1000);
        return date.toLocaleString("ro-RO", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        });
    }

    return null;
}

/**
 * Formats a Unix timestamp to compact Romanian date/time format
 * @param unixTimestamp - Unix timestamp in seconds
 * @returns Formatted string as "HH:MM DD.MM.YY" or empty string if invalid
 * @example formatSchoolUpdatedAt(1640995200) => "00:00 01.01.22"
 */
export function formatSchoolUpdatedAt(unixTimestamp: number | null | undefined): string {
    if (!unixTimestamp || isNaN(unixTimestamp)) return "";

    const date = new Date(unixTimestamp * 1000);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${hours}:${minutes} ${day}.${month}.${year}`;
}

/**
 * Capitalizes the first letter of a string
 * @param str - String to capitalize
 * @returns Capitalized string
 * @example capitalizeName("john") => "John"
 */
export function capitalizeName(str: string): string {
    if (typeof str !== "string") return "";
    if (str.length === 1) return str.toUpperCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
}
